import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useWorkspace } from './WorkspaceContext';
import { Users, Loader2, Trash2, Send } from 'lucide-react';
import logo from './logo.png';

const AVATAR_COLORS = ['#dc2626', '#2563eb', '#16a34a', '#7c3aed', '#d97706', '#0891b2'];

function avatarColor(name) {
  return AVATAR_COLORS[(name || '?').charCodeAt(0) % AVATAR_COLORS.length];
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
}

function activityText(a) {
  const name = (a.user_name || 'Someone').split(' ')[0];
  const entity = a.entity_name || '';
  switch (a.action) {
    case 'analysed_deck': return `${name} analysed ${entity} deck`;
    case 'added_company': return `${name} added ${entity} to portfolio`;
    case 'wrote_memo': return `${name} wrote investment memo for ${entity}`;
    case 'added_note': return `${name} added a note to ${entity}`;
    case 'commented': return `${name} commented on ${entity}`;
    case 'invited_member': return `${name} invited ${entity} as ${a.metadata?.role || 'member'}`;
    default: return `${name} performed an action`;
  }
}

function rolePillStyle(role) {
  if (role === 'Partner') return { background: 'rgba(220,38,38,0.12)', color: '#dc2626' };
  if (role === 'Associate') return { background: 'rgba(37,99,235,0.12)', color: '#2563eb' };
  return { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' };
}

function friendlyError(msg = '') {
  const m = (msg || '').toLowerCase();
  if (m.includes('row-level security') || m.includes('rls'))
    return "You don't have permission to invite members. Only Partners can invite team members.";
  if (m.includes('duplicate') || m.includes('already exists'))
    return "This person has already been invited.";
  if (m.includes('invalid') || m.includes('not found'))
    return "Please check the email address and try again.";
  return "Something went wrong. Please try again.";
}

export default function TeamPage() {
  const ctx = useWorkspace();
  const workspace = ctx?.workspace;
  const members = ctx?.members || [];
  const userRole = ctx?.userRole;
  const loading = ctx?.loading;
  const inviteMember = ctx?.inviteMember;

  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Associate');
  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setCurrentUserId(session.user.id);
    });
  }, []);

  useEffect(() => {
    if (workspace?.id) fetchActivities();
  }, [workspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    const { data } = await supabase
      .from('activity_feed')
      .select('*')
      .eq('workspace_id', workspace.id)
      .order('created_at', { ascending: false })
      .limit(50);
    setActivities(data || []);
    setActivitiesLoading(false);
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member?')) return;
    await supabase.from('workspace_members').delete().eq('id', memberId);
    window.location.reload();
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || inviting) return;
    setInviting(true);
    setInviteMessage(null);
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      setInviteMessage({ type: 'success', text: `Invite sent to ${inviteEmail.trim()}` });
      setInviteEmail('');
    } catch (err) {
      setInviteMessage({ type: 'error', text: friendlyError(err.message) });
    } finally {
      setInviting(false);
    }
  };

  const labelStyle = { fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, outline: 'none', fontSize: 13, fontWeight: 500, color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', flexDirection: 'column', gap: 16, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <Loader2 style={{ width: 28, height: 28, color: '#dc2626' }} className="animate-spin" />
        <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.4)', margin: 0 }}>Loading workspace...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', fontFamily: "'DM Sans', system-ui, sans-serif", display: 'flex', flexDirection: 'column', gap: 32 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <img src={logo} style={{ width: 28, height: 28 }} alt="Binos" />
        <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users style={{ width: 18, height: 18, color: '#dc2626' }} />
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: '#fff', margin: 0, letterSpacing: '-0.5px' }}>Your Team</h1>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: 0, fontWeight: 500 }}>{members.length} member{members.length !== 1 ? 's' : ''} · {workspace?.fund_name || 'Fund Workspace'}</p>
          </div>
        </div>
      </div>

      {/* SECTION 1 — Members Table */}
      <div style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>Members</p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
              {['Member', 'Role', 'Joined', ''].map((h, i) => (
                <th key={i} style={{ padding: '10px 24px', textAlign: 'left', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((m, i) => (
              <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '14px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarColor(m.full_name || m.email), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                      {(m.full_name || m.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: 0 }}>{m.full_name || '—'}</p>
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: 0, fontWeight: 400 }}>{m.email}</p>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '14px 24px' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 100, ...rolePillStyle(m.role) }}>
                    {m.role}
                  </span>
                </td>
                <td style={{ padding: '14px 24px', fontSize: 12, color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
                  {m.created_at ? new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </td>
                <td style={{ padding: '14px 24px', textAlign: 'right' }}>
                  {userRole === 'Partner' && m.user_id !== currentUserId && (
                    <button
                      onClick={() => handleRemoveMember(m.id)}
                      style={{ padding: '6px 8px', background: 'none', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, cursor: 'pointer', color: 'rgba(220,38,38,0.5)', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.1)'; e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(220,38,38,0.5)'; }}
                    >
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SECTION 2 — Invite Member (Partners only) */}
      {userRole === 'Partner' && (
        <div style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: '24px' }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 20px' }}>Invite Member</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px auto', gap: 12, alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleInvite()}
                placeholder="colleague@fund.com"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#dc2626'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
              />
            </div>
            <div>
              <label style={labelStyle}>Role</label>
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="Associate">Associate</option>
                <option value="Analyst">Analyst</option>
              </select>
            </div>
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              style={{ padding: '10px 20px', background: '#dc2626', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 800, color: '#fff', cursor: inviting || !inviteEmail.trim() ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: inviting || !inviteEmail.trim() ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
            >
              <Send style={{ width: 13, height: 13 }} />
              {inviting ? 'Sending...' : 'Send Invite →'}
            </button>
          </div>
          {inviteMessage && (
            <p style={{ fontSize: 12, fontWeight: 600, color: inviteMessage.type === 'success' ? '#16a34a' : '#dc2626', margin: '12px 0 0' }}>
              {inviteMessage.text}
            </p>
          )}
        </div>
      )}

      {/* SECTION 3 — Activity Feed */}
      <div style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>Team Activity</p>
        </div>

        {activitiesLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: 12 }}>
            <Loader2 style={{ width: 18, height: 18, color: '#dc2626' }} className="animate-spin" />
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', margin: 0 }}>Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.2)', margin: '0 0 4px' }}>No activity yet</p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.12)', fontWeight: 500, margin: 0 }}>Activity will appear here as your team uses Binos</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {activities.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 24px', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: avatarColor(a.user_name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                  {(a.user_name || '?').charAt(0).toUpperCase()}
                </div>
                <p style={{ flex: 1, fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: 0, fontWeight: 400 }}>
                  {activityText(a)}
                </p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', margin: 0, fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {timeAgo(a.created_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
